"use client";
import ArticleBody from "@/components/ArticleBody";
import { articlePresentation } from "@/lib/articles/presentation";
import ArticleImageCaption from "@/components/ArticleImageCaption";

import { authorPath } from '@/lib/articles/normalizeAuthors';
import React from 'react';
import Link from 'next/link';
import { AtSign } from 'lucide-react';
import AdSlotWrapper from '@/components/AdSlotWrapper';
import CommentsDrawer from './CommentsDrawer';


export default function Article({ initialArticle, settings = {}, publicationId, latestArticles: _latestArticles = [], articleUrl }) {
  const article = initialArticle;
  const presentation = articlePresentation(article || {});
  
  if (!article) return null;

  let prefix = '';
  const generalSettings = typeof settings?.general === 'string' ? JSON.parse(settings.general) : settings?.general;
  if (generalSettings?.routePrefix) {
    prefix = `/${generalSettings.routePrefix}`;
  }

  // Format dates
  const pubDate = new Date(article.date || article.publishedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const pubTime = new Date(article.date || article.publishedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  
  return (
    <div className="w-full pb-16 font-sans">
      
      {/* Top Ad Slot */}
      <div className="max-w-[980px] mx-auto w-full mb-10 flex justify-center">
        <AdSlotWrapper id="article_top_1" style={{ minHeight: '90px' }} settings={settings} />
      </div>

      <article className="w-full">
        
        {/* CENTERED ARTICLE HEADER (920px) */}
        <header className="max-w-[620px] mx-auto w-full text-left px-4 mb-8">
          <Link href={`${prefix}/category/${article.category.toLowerCase().replace(/ /g, '-').replace(/&/g, 'and')}`} className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest text-black border border-black hover:bg-black hover:text-white transition-colors mb-6 font-sans">
            {article.category}
          </Link>
          
          <h1 className="font-classic text-[clamp(42px,4vw,56px)] font-bold leading-[1.05] tracking-tight text-gray-900 mb-6">
            {article.title}
          </h1>

          {presentation.subtitle && (
            <p data-article-subtitle className="text-[19px] md:text-[21px] font-normal leading-[1.45] text-gray-800 mb-6 font-classic">
              {presentation.subtitle}
            </p>
          )}

          <div className="flex flex-col md:flex-row md:items-center py-4 border-t border-b border-gray-200 gap-4 mt-6">
            <div className="flex flex-col md:flex-row gap-4 w-full">
              {article.authors && article.authors.length > 0 ? (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-gray-900 text-sm uppercase tracking-wide font-sans">By</span>
                    {article.authors.map((auth, idx) => (
                      <React.Fragment key={auth.id}>
                        {idx > 0 && idx === article.authors.length - 1 ? (
                          <span className="font-bold text-gray-900 text-sm uppercase tracking-wide font-sans"> and </span>
                        ) : idx > 0 ? (
                          <span className="font-bold text-gray-900 text-sm uppercase tracking-wide font-sans">, </span>
                        ) : null}
                        <Link href={authorPath(auth, { settings }) || "#"} className="font-bold text-gray-900 text-sm uppercase tracking-wide hover:text-gray-600 transition-colors block font-sans">
                          {auth.name}
                        </Link>
                      </React.Fragment>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    {article.authors.map(auth => (
                      auth.image ? (
                        <Link key={'img-'+auth.id} href={authorPath(auth, { settings }) || "#"} className="block shrink-0">
                          <img src={auth.image} alt={auth.name} className="w-10 h-10 md:w-11 md:h-11 rounded-full object-cover border border-gray-200 hover:border-gray-400 transition-colors" />
                        </Link>
                      ) : null
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <Link href={article.authorUrl || authorPath({ id: article.authorId, name: article.author }, { settings }) || "#"} className="font-bold text-gray-900 text-sm uppercase tracking-wide hover:text-gray-600 transition-colors block font-sans">
                    By {article.author}
                  </Link>
                </div>
              )}
              
              <div className="md:ml-auto md:text-right mt-2 md:mt-0 flex flex-col justify-center">
                <time className="text-gray-500 text-xs font-bold uppercase tracking-widest font-sans block">
                  Published {pubDate} at {pubTime}
                </time>
              </div>
            </div>
          </div>
        </header>

        {/* CENTERED SHARE CONTROLS (740px) */}
        <div className="max-w-[620px] mx-auto w-full text-left px-4 mb-8">
          <div className="flex items-center gap-2 border-b border-gray-200 pb-4">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-widest mr-2 font-sans">Share:</span>
            
            {/* Facebook */}
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all duration-300" title="Share on Facebook">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            
            {/* X (Twitter) */}
            <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(article.title)}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all duration-300" title="Share on X">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            </a>

            {/* Threads */}
            <a href={`https://threads.net/intent/post?text=${encodeURIComponent(article.title + ' ' + (articleUrl))}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all duration-300" title="Share on Threads">
              <AtSign className="w-3.5 h-3.5" />
            </a>

            {/* WhatsApp */}
            <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(article.title + ' ' + (articleUrl))}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all duration-300" title="Share on WhatsApp">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
            </a>
          </div>
        </div>

        {/* CENTERED FEATURED IMAGE (980px) */}
        {article.image && (
          <figure className="max-w-[620px] mx-auto w-full px-4 mb-10">
            <div className="w-full aspect-video bg-gray-100 overflow-hidden relative">
              <img 
                src={article.image} 
                alt={presentation.imageAlt}
                className="w-full h-full object-cover" 
              />
            </div>
            <ArticleImageCaption article={article} className="mt-3 flex justify-between text-xs text-gray-500 font-sans px-2" creditClassName="uppercase font-bold tracking-widest" />
          </figure>
        )}

        {/* CENTERED ARTICLE BODY (740px) */}
        <div className="max-w-[620px] mx-auto w-full px-4">
          <ArticleBody publicationSettings={settings}
            className="prose prose-lg max-w-none text-[18px] md:text-[20px] leading-[1.7] text-gray-900 font-sans
              prose-h2:font-classic prose-h2:font-bold prose-h2:text-[28px] md:prose-h2:text-[32px] prose-h2:mb-4 prose-h2:mt-10
              prose-h3:font-classic prose-h3:font-bold prose-h3:text-[22px] md:prose-h3:text-[26px] prose-h3:mb-3 prose-h3:mt-8
              prose-p:mb-6 prose-p:text-gray-900
              prose-a:text-black prose-a:underline prose-a:underline-offset-4 hover:prose-a:text-gray-600
              prose-blockquote:font-classic prose-blockquote:text-[22px] md:prose-blockquote:text-[26px] prose-blockquote:font-normal prose-blockquote:italic prose-blockquote:text-gray-800 prose-blockquote:border-l-2 prose-blockquote:border-black prose-blockquote:pl-6 prose-blockquote:my-10
              prose-img:max-w-full prose-img:w-full prose-img:mb-4
            "
            html={article.content}
          />

          {/* Ad space inside article body overflow logic */}
          {Object.keys(settings?.ads?.slots || {})
            .filter(id => id.startsWith('article_inline_') && parseInt(id.replace('article_inline_', '')) > 1)
            .sort((a, b) => parseInt(a.replace('article_inline_', '')) - parseInt(b.replace('article_inline_', '')))
            .map(id => (
              <div key={id} className="my-10 flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-widest text-gray-400 block text-center mb-3">Advertisement</span>
                <AdSlotWrapper id={id} className="w-full" style={{ minHeight: '250px' }} settings={settings} />
              </div>
          ))}
          
          {/* Article Footer Tags */}
          <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-gray-200">
            <span className="font-bold text-xs uppercase tracking-widest mr-2 text-gray-900 flex items-center">Topics:</span>
            {(article.tags || ['News']).map((tag, i) => (
              <span key={i} className="border border-gray-300 text-gray-700 px-3 py-1 text-xs font-bold uppercase tracking-widest">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Ad space before bottom */}
        <div className="max-w-[980px] mx-auto w-full px-4 my-16 flex justify-center">
          <AdSlotWrapper id="article_before_bottom" style={{ minHeight: '90px' }} settings={settings} />
        </div>

      <CommentsDrawer articleId={article.id} publicationId={publicationId} currentPrefix={prefix} />
      </article>
    </div>
  );
}










